import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: "https://vervestacks.cloud:3001/api",
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: auth token + start timer
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.metadata = { startTime: performance.now() };
    return config;
  },
  (error) => Promise.reject(error)
);

// Emit timing event for on-page display (cached: true = HIT, false = MISS/none)
const emitTiming = (label, durationMs, ok, cacheStatus) => {
  const cached = cacheStatus === 'HIT';
  window.dispatchEvent(new CustomEvent('api-timing', {
    detail: { label, duration: Math.round(durationMs), ok, cached, cacheStatus: cacheStatus || null, ts: Date.now() }
  }));
};

const extractLabel = (config) => {
  const url = (config.url || '').replace(/^\//, '');
  const method = (config.method || 'get').toUpperCase();
  const short = url
    .replace('generation-profile/', '')
    .replace('capacity/', '')
    .replace('renewable-potential/', '')
    .split('?')[0];
  return `${method} ${short}`;
};

// Response interceptor: emit timing + handle errors
api.interceptors.response.use(
  (response) => {
    const start = response.config?.metadata?.startTime;
    const cacheStatus = response.headers?.['x-cache'] ?? null;
    if (start) emitTiming(extractLabel(response.config), performance.now() - start, true, cacheStatus);
    return response;
  },
  (error) => {
    const start = error.config?.metadata?.startTime;
    if (start) emitTiming(extractLabel(error.config), performance.now() - start, false);
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
};

// VedaOnline proxy API
export const vedaonlineAPI = {
  getModelNames: async (isoCode) => {
    const response = await api.get('/vedaonline/models/names', {
      params: { iso: isoCode }
    });
    return response.data;
  },

  getModelViews: async (modelName) => {
    const response = await api.get('/vedaonline/model-views', {
      params: { modelName }
    });
    return response.data;
  },

  getViewComments: async (viewId) => {
    const response = await api.get('/vedaonline/view-comments', {
      params: { viewId }
    });
    return response.data;
  },

  getCapacityMix: async (params) => {
    const response = await api.get('/vedaonline/capacity-mix', {
      params,
      timeout: 120000,
    });
    return response.data;
  },
};

// Countries API functions
export const countriesAPI = {
  getAll: async () => {
    const response = await api.get('/countries');
    return response.data;
  },
  
  getWithModels: async () => {
    const response = await api.get('/countries/with-models');
    return response.data;
  },
  
  getByIso: async (isoCode) => {
    const response = await api.get(`/countries/${isoCode}`);
    return response.data;
  },

  getMetadata: async (isoCode) => {
    const response = await api.get(`/countries/metadata/${isoCode.toUpperCase()}`);
    return response.data;
  },
  
  getByRegion: async (region) => {
    const response = await api.get(`/countries/region/${region}`);
    return response.data;
  },
};

// Capacity API functions
export const capacityAPI = {
  getCapacityByFuel: async (isoCode, year) => {
    const response = await api.post('/capacity/capacity-by-fuel', { iso_code: isoCode, year });
    return response.data;
  },
  
  getCapacityUtilization: async (isoCode, year = 2022) => {
    const response = await api.get(`/overview/capacity-utilization/${isoCode}?year=${year}`);
    return response.data;
  },

  // Fuel Colors API
  async getFuelColors() {
    const response = await api.get('/capacity/fuel-colors');
    return response.data;
  },

  // Must-run CF (utilization factors for dispatch simulation)
  getMustRunCF: async (isoCode, year) => {
    const response = await api.post('/capacity/must-run-cf', { iso_code: isoCode, year });
    return response.data;
  }
};

export const simulatorAPI = {
  getTechMapping: async () => {
    const response = await api.get('/simulator/tech-mapping');
    return response.data;
  },
  getVoFlexTechCatalog: async () => {
    const response = await api.get('/simulator/vo-flex-tech-catalog');
    return response.data;
  },
};

// Energy Metrics API functions
export const energyMetricsAPI = {
  getEnergyMetrics: async (isoCode) => {
    const response = await api.get(`/overview/energy-metrics/${isoCode}`);
    return response.data;
  },
  getExistingStockMetrics: async (isoCode) => {
    const response = await api.get(`/overview/existing-stock/${isoCode}`);
    return response.data;
  },
  getAr6ScenarioDrivers: async (isoCode) => {
    const response = await api.get(`/overview/ar6-scenario/${isoCode}`);
    return response.data;
  },
};

// Health check
export const healthCheck = async () => {
  const response = await axios.get(`${API_BASE_URL.replace('/api', '')}/health`);
  return response.data;
};

// Renewable Potential API
export const renewablePotentialAPI = {
  getSolarZones: async (isoCode) => {
    const response = await axios.get(`${API_BASE_URL}/renewable-potential/solar-zones/${isoCode}`);
    return response.data;
  },
  getSolarClusters: async (isoCode) => {
    const response = await axios.get(`${API_BASE_URL}/renewable-potential/solar-clusters/${isoCode}`);
    return response.data;
  },
  getWindZones: async (isoCode, windType = 'onshore') => {
    const response = await axios.get(`${API_BASE_URL}/renewable-potential/wind-zones/${isoCode}?wind_type=${windType}`);
    return response.data;
  },

  /**
   * Maximum installable capacity potential (GW) for the country, computed from
   * the renewable_zones_solar table on the backend (SUM of installed_capacity
   * potential MW, converted to GW).
   */
  getSolarCapacityLimit: async (isoCode) => {
    const response = await api.get(`/renewable-potential/capacity-limit/solar/${isoCode}`);
    return response.data;
  },
  getWindOnshoreCapacityLimit: async (isoCode) => {
    const response = await api.get(`/renewable-potential/capacity-limit/wind-onshore/${isoCode}`);
    return response.data;
  },
  getWindOffshoreCapacityLimit: async (isoCode) => {
    const response = await api.get(`/renewable-potential/capacity-limit/wind-offshore/${isoCode}`);
    return response.data;
  },

  getWindClusters: async (isoCode, windType = 'onshore') => {
    const response = await axios.get(`${API_BASE_URL}/renewable-potential/wind-clusters/${isoCode}?wind_type=${windType}`);
    return response.data;
  },
  
  healthCheck: async () => {
    const response = await axios.get(`${API_BASE_URL}/renewable-potential/health`);
    return response.data;
  }
};

// Transmission API
export const transmissionAPI = {
  getTransmissionClusterDefault: async (isoCode) => {
    const response = await axios.get(`${API_BASE_URL}/transmission/cluster-default/${isoCode}`);
    return response.data;
  },

  getTransmissionData: async (isoCode, clusters = null) => {
    const url = clusters
      ? `${API_BASE_URL}/transmission/data/${isoCode}?clusters=${clusters}`
      : `${API_BASE_URL}/transmission/data/${isoCode}`;
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const response = await axios.get(url);
        return response.data;
      } catch (err) {
        const status = err.response?.status;
        const retryable = status === 503 || status === 502 || status === 429;
        if (attempt < maxAttempts && retryable) {
          await new Promise((resolve) => setTimeout(resolve, 600 * attempt));
          continue;
        }
        throw err;
      }
    }
    return null;
  },
  
  getTransmissionNetworkData: async (isoCode) => {
    const response = await axios.get(`${API_BASE_URL}/transmission/network/${isoCode}`);
    return response.data;
  },
  
  getTransmissionGenerationData: async (isoCode) => {
    const response = await axios.get(`${API_BASE_URL}/transmission/generation/${isoCode}`);
    return response.data;
  },
  
  healthCheck: async () => {
    const response = await axios.get(`${API_BASE_URL}/transmission/health`);
    return response.data;
  }
};

export const gasPipelinesAPI = {
  getGasPipelines: async (isoCode) => {
    const response = await axios.get(`${API_BASE_URL}/gas-pipelines/${isoCode}`);
    return response.data;
  },
};

// Generation Profile (Hourly by fuel)
export const generationProfileAPI = {
  getSolarHourly: async (isoCode, year, capacityGw, weatherYear) => {
    const normalizedWeatherYear = Number(weatherYear);
    const payload = {
      isoCode,
      year,
      capacityGw
    };
    if (Number.isFinite(normalizedWeatherYear)) {
      payload.weatherYear = normalizedWeatherYear;
    }
    const response = await api.post(`/generation-profile/solar-hourly`, {
      ...payload
    }, { timeout: 120000 });
    return response.data;
  },
  getWindHourly: async (isoCode, year, capacityGw, weatherYear) => {
    const normalizedWeatherYear = Number(weatherYear);
    const payload = {
      isoCode,
      year,
      capacityGw
    };
    if (Number.isFinite(normalizedWeatherYear)) {
      payload.weatherYear = normalizedWeatherYear;
    }
    const response = await api.post('/generation-profile/wind-hourly', {
      ...payload
    }, { timeout: 120000 });
    return response.data;
  },
  getWindOffHourly: async (isoCode, year, capacityGw, weatherYear) => {
    const normalizedWeatherYear = Number(weatherYear);
    const payload = {
      isoCode,
      year,
      capacityGw
    };
    if (Number.isFinite(normalizedWeatherYear)) {
      payload.weatherYear = normalizedWeatherYear;
    }
    const response = await api.post('/generation-profile/windoff-hourly', {
      ...payload
    }, { timeout: 120000 });
    return response.data;
  },
  /**
   * VO Type-2: combine cluster GW (from capacity engine) with DB cluster CF profiles.
   */
  getVoVreProfile: async ({ isoCode, technology, clusters, weatherYear, clusterParam }) => {
    const payload = { isoCode, technology, clusters };
    const normalizedWeatherYear = Number(weatherYear);
    if (Number.isFinite(normalizedWeatherYear)) {
      payload.weatherYear = normalizedWeatherYear;
    }
    if (clusterParam) payload.clusterParam = clusterParam;
    const response = await api.post('/generation-profile/vo-vre-profile', payload, { timeout: 120000 });
    return response.data;
  },
  getNuclearHourly: async (isoCode, capacityGw = null) => {
    const response = await api.post(`/generation-profile/nuclear-hourly`, {
      iso_code: isoCode,
      nuclear_capacity_override_gw: capacityGw
    }, {
      timeout: 120000
    });
    return response.data;
  },
  /**
   * Fetch hydro monthly energy budgets (12 MWh values) for the simulator.
   * @param {string} isoCode      - 3-letter ISO code
   * @param {number} capacityGw   - hydro capacity in GW (from capacity-by-fuel)
   * @param {number|null} [year]  - optional weather year; when provided, the backend
   *                                walks the weather-year-sensitive fallback ladder
   *                                (monthly-direct → annual×climatology → annual×uniform
   *                                → climatology → cf-proxy). Omit for climatology.
   *                                See HYDRO_WEATHER_YEAR_METHODOLOGY.md
   */
  getHydroMonthlyBudget: async (isoCode, capacityGw, year = null) => {
    if (typeof capacityGw !== 'number' || capacityGw < 0) {
      throw new Error('getHydroMonthlyBudget requires capacityGw (>= 0) from capacity-by-fuel');
    }
    const body = { isoCode, capacity_gw: capacityGw };
    if (Number.isInteger(year)) body.year = year;
    const response = await api.post('/generation-profile/hydro-budget', body, {
      timeout: 30000
    });
    return response.data;
  },
  getWeatherYears: async (isoCode) => {
    const response = await api.post('/generation-profile/weather-years', { isoCode }, {
      timeout: 30000
    });
    return response.data;
  }
};

// GitHub API functions
export const githubAPI = {
  getBranches: async (isoCode) => {
    const response = await api.get(`/github/branches/${isoCode}`);
    return response.data;
  },
  
  getAvailableIsos: async () => {
    const response = await api.get('/github/available-isos');
    return response.data;
  }
};

const SYNC_DATA_TOKEN_KEY = 'vervestacks-sync-data-token';
const SYNC_DATA_SHARE_SESSION_KEY = 'vervestacks-sync-data-share-session';

const syncDataHeaders = () => {
  if (typeof sessionStorage === 'undefined') return {};
  const headers = {};
  const token = sessionStorage.getItem(SYNC_DATA_TOKEN_KEY);
  const shareSession = sessionStorage.getItem(SYNC_DATA_SHARE_SESSION_KEY);
  if (token) headers['X-Sync-Data-Token'] = token;
  if (shareSession) headers['X-Sync-Data-Share-Session'] = shareSession;
  return headers;
};

export const syncDataAPI = {
  tokenKey: SYNC_DATA_TOKEN_KEY,
  shareSessionKey: SYNC_DATA_SHARE_SESSION_KEY,
  getStatus: async () => {
    const response = await api.get('/sync-data/status', {
      headers: syncDataHeaders(),
      timeout: 30000
    });
    return response.data;
  },
  connectShare: async (password) => {
    const response = await api.post('/sync-data/connect', { password }, {
      headers: syncDataHeaders(),
      timeout: 30000
    });
    if (response.data?.shareSession && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SYNC_DATA_SHARE_SESSION_KEY, response.data.shareSession);
    }
    return response.data;
  },
  clearShareSession: () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SYNC_DATA_SHARE_SESSION_KEY);
    }
  },
  copy: async (target = '') => {
    const response = await api.post('/sync-data/copy', { target }, {
      headers: syncDataHeaders(),
      timeout: 120000
    });
    return response.data;
  },
  publish: async () => {
    const response = await api.post('/sync-data/publish', {}, {
      headers: syncDataHeaders(),
      timeout: 120000
    });
    return response.data;
  }
};

export default api;
